import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type { ModuleSettingsEntry } from "../types";

interface UseModuleSettingsResult {
  readonly entries: readonly ModuleSettingsEntry[];
  readonly loading: boolean;
  readonly updateSetting: (
    moduleId: string,
    key: string,
    value: string,
  ) => Promise<void>;
  readonly reload: () => Promise<void>;
}

export function useModuleSettings(): UseModuleSettingsResult {
  const runtime = useAppRuntime();
  const [entries, setEntries] = useState<ModuleSettingsEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const result = await runtime.transport.rpc<ModuleSettingsEntry[]>(
        "module_settings.list",
      );
      setEntries(result);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const updateSetting = useCallback(
    async (moduleId: string, key: string, value: string): Promise<void> => {
      await runtime.transport.rpc("module_settings.update", {
        module_id: moduleId,
        values: { [key]: value },
      });
      // Update local state optimistically
      setEntries((prev) =>
        prev.map((entry) => {
          if (entry.module_id !== moduleId) return entry;
          return {
            ...entry,
            values: entry.values.map((v) =>
              v.key === key ? { ...v, value } : v,
            ),
          };
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { entries, loading, updateSetting, reload: load };
}
